import { useAdministratorField } from 'hooks/useAdministratorField';
import type { EmployeePickerElement, EmployeePickerForm } from 'types/back-office-settings';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { CONFIGURATION_CARD_CLASS, SETTING_GROUP_BAND_CLASS } from '../configurationCard';
import { ListedInPopover } from './ListedInPopover';
import { DIVIDER_CLASS, SettingChip } from './RequiredFieldsCard';

const COPY = 'back-office-settings.employee-pickers';

const FORMS: EmployeePickerForm[] = ['CONTACT', 'CASE', 'GROUP', 'EMPLOYEE', 'DELEGATION'];

/** Each row's name is the label its form shows the picker under; the Assistant rows take the firm's name for it. */
const ROW_LABELS: Record<string, string> = {
    'CONTACT:RELATIONSHIP_PARTNER': 'contacts.about.relationship-partner',
    'CONTACT:RELATIONSHIP_MANAGER': 'contacts.about.relationship-manager',
    'CONTACT:ASSISTANT': 'contacts.about.client-assistant',
    'CASE:SUPERVISED_BY': 'matters.supervised-by',
    'CASE:ASSISTANT': 'matters.case-assistant',
    'GROUP:RELATIONSHIP_PARTNER': 'groups.group-relationship-partners',
    'GROUP:RELATIONSHIP_MANAGER': 'groups.group-relationship-managers',
    'EMPLOYEE:SUPERVISOR': 'supervisor',
    'DELEGATION:SUBSTITUTE_SUPERVISING_PARTNER': `${COPY}.substitute-supervising-partner`,
};

interface Props {
    entries: EmployeePickerElement[];
    /** Without UPDATE.BACK_OFFICE_SETTINGS: drawn dimmed, every chip off. */
    readOnly: boolean;
}

/** Who each employee picker lists, a band per form; the chip opens the row's "Listed in" popover. */
export function EmployeePickersCard({ entries, readOnly }: Props) {
    const { name } = useAdministratorField();
    return (
        <div className={cn(CONFIGURATION_CARD_CLASS, 'new:rounded-[10px]', readOnly && 'opacity-60')}>
            <div className={SETTING_GROUP_BAND_CLASS}>{t(`${COPY}.title`)}</div>
            <p className={cn(DIVIDER_CLASS, 'text-grey-600 m-0 border-t px-5 py-3 text-[13px] leading-[19px]')}>
                {t(`${COPY}.description`)}
            </p>
            {FORMS.map((form) => {
                const rows = entries.filter((entry) => entry.form === form);
                if (rows.length === 0) return null;
                const formName = t(`${COPY}.forms.${form}`);
                return (
                    <div key={form}>
                        <div className={cn(SETTING_GROUP_BAND_CLASS, DIVIDER_CLASS, 'border-t')}>{formName}</div>
                        {rows.map((entry) => {
                            const label = t(ROW_LABELS[`${entry.form}:${entry.field}`] ?? entry.field, { name });
                            return (
                                <div
                                    key={entry.field}
                                    className={cn(
                                        DIVIDER_CLASS,
                                        'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 border-t px-5 py-3 max-sm:px-4'
                                    )}
                                >
                                    <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
                                        <span className="text-fg text-sm leading-5 font-medium">{label}</span>
                                        {entry.setting ? <SettingChip setting={entry.setting} /> : null}
                                    </span>
                                    <ListedInPopover
                                        entry={entry}
                                        context={t(`${COPY}.context`, { form: formName, field: label })}
                                        readOnly={readOnly}
                                    />
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
}
